import {useEffect, useState} from 'react'
import Loader from '../../custom/loader/Loader'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../../redux/hooks'
import {Planning, StudentNews} from '../../../redux/student/studentSlice'

const StudentDashboard = () => {
  const [loader, setLoader] = useState(true)
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const [news, setNews] = useState<any[]>([])
  const [planning, setPlanning] = useState<any>(null)
  const [expandedNews, setExpandedNews] = useState<{[key: string]: boolean}>({})
  const [openSemester, setOpenSemester] = useState<number | null>(null)

  useEffect(() => {
    dispatch(StudentNews()).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setNews(res.payload)
        const initialExpandedState = res.payload.reduce((acc: any, item: any) => {
          acc[item.id] = false
          return acc
        }, {})
        setExpandedNews(initialExpandedState)
      }
      setLoader(false)
    })

    dispatch(Planning()).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') setPlanning(res.payload)
      setLoader(false)
    })
  }, [])

  // Safer date formatting with fallback
  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return '' // Return empty string for undefined/null dates

    try {
      const options: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }
      return new Date(dateString).toLocaleDateString('en-GB', options).replace(',', '')
    } catch (e) {
      console.error('Error formatting date:', e)
      return dateString // Return original string if formatting fails
    }
  }

  // Safer content processing
  const processContent = (content: string | undefined, isExpanded: boolean) => {
    if (!content) return ''

    if (isExpanded) return content // full content with HTML

    const maxLength = 300

    const div = document.createElement('div')
    div.innerHTML = content

    let currentLength = 0
    let result = ''

    const walkNodes = (nodes: any): boolean => {
      for (let node of nodes) {
        if (currentLength >= maxLength) return false

        if (node.nodeType === Node.TEXT_NODE) {
          const remaining = maxLength - currentLength
          const text = node.nodeValue?.substring(0, remaining) || ''
          result += text
          currentLength += text.length
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName.toLowerCase()
          // Keep only these tags
          if (['b', 'i', 'u', 'strong', 'em', 'p', 'br', 'ul', 'li'].includes(tagName)) {
            result += `<${tagName}>`
            const keepWalking = walkNodes(node.childNodes)
            result += `</${tagName}>`
            if (!keepWalking) return false
          } else {
            // Skip unknown tags but keep children
            const keepWalking = walkNodes(node.childNodes)
            if (!keepWalking) return false
          }
        }
      }
      return true
    }

    walkNodes(div.childNodes)

    return result + '...'
  }

  const toggleExpand = (id: string) => {
    setExpandedNews((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  function toSortableDate(dateStr: string): string {
    const [day, month, year] = dateStr.split('-')
    return `${year}-${month}-${day}`
  }

  if (loader) return <Loader />

  return (
    <>
      {planning && planning.semesters && planning.semesters.length > 0 && (
        <div className='my-3'>
          {planning.semesters.map((sem: any, index: number) => {
            const collapseId = `semesterCollapse${index}`
            const isOpen = openSemester === index

            return (
              <div key={index} className='card my-3 shadow-sm'>
                <div
                  className='card-header bg-primary text-white d-flex justify-content-between align-items-center'
                  style={{cursor: 'pointer', minHeight: '40px'}}
                  data-bs-toggle='collapse'
                  data-bs-target={`#${collapseId}`}
                  aria-expanded={isOpen}
                  onClick={() => setOpenSemester(isOpen ? null : index)}
                >
                  <span className='fw-bold fs-4'>
                    {t('LABEL.ACADEMIC_YEAR')}: <strong>{planning.academic_year}</strong>
                    <span className='mx-3'>
                      {t('LABEL.SEMESTER')}: <strong>{t('LABEL.' + sem.semester)}</strong>
                    </span>
                    <span className='mx-3'>{t('LABEL.COURSE_PLANNING')}</span>
                  </span>
                  <i
                    className={`fas fa-chevron-${isOpen ? 'up' : 'down'} text-white fw-bold`}
                    style={{
                      transition: '0.5',
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  ></i>
                </div>

                <div className={`collapse ${isOpen ? 'show' : ''}`} id={collapseId}>
                  <div className='card-body'>
                    <div className='table-responsive'>
                      <table className='table table-bordered align-middle'>
                        <thead className='bg-primary'>
                          <tr>
                            <th className='text-white' style={{width: '3%'}}>
                              #
                            </th>
                            <th className='text-white' style={{width: '20%'}}>
                              {t('LABEL.COURSE')}
                            </th>
                            <th className='text-white' style={{width: '15%'}}>
                              {t('LABEL.LECTURER')}
                            </th>
                            <th className='text-white' style={{width: '50%'}}>
                              {t('LABEL.DATES')}
                            </th>
                            <th className='text-white' style={{width: '12%'}}>
                              {t('LABEL.EXAM_DATE_TIME')}
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {sem.courses.length > 0 ? (
                            sem.courses.map((course: any, i: number) => (
                              <tr key={course.id}>
                                <th className='text-center bg-primary text-white'>{i + 1}</th>

                                <td>
                                  {course.name}
                                  <br />
                                  <span className='text-muted'>{course.code}</span>
                                </td>

                                <td>{course.lecturer_name}</td>

                                <td>
                                  {course.dates
                                    .slice()
                                    .sort((a: any, b: any) => {
                                      const dateA = new Date(toSortableDate(a.date)).getTime()
                                      const dateB = new Date(toSortableDate(b.date)).getTime()
                                      return dateA - dateB
                                    })
                                    .map((date: any, idx: number) => (
                                      <span
                                        key={idx}
                                        className='badge bg-primary text-white m-1'
                                        style={{whiteSpace: 'pre-line'}}
                                      >
                                        {date.date}
                                        {'\n'}
                                        {date.time_slot}
                                      </span>
                                    ))}
                                </td>

                                <td>{course.exam_date}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className='text-center'>
                                {t('LABEL.NO_COURSES')}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div className='container-fluid p-0'>
        <div className='card mb-5 mb-xl-10 mt-0 shadow-sm rounded-4 border-0'>
          <div className='card-header border-0 bg-white py-4 rounded-top-4'>
            <h3 className='fw-bold text-primary m-0'>{t('LABEL.NEWS_AND_ANNOUNCEMENT')}</h3>
          </div>

          {/* No Data */}
          {news.length < 1 && (
            <div className='card-body'>
              <h4 className='text-center text-danger fw-bold py-5 m-0'>
                {t('GLOBAL.DATA_NOT_AVAILABLE')}
              </h4>
            </div>
          )}

          {/* News List */}
          {news.length > 0 && (
            <div className='card-body py-4'>
              <div className='timeline'>
                {news.map((item: any) => (
                  <div key={item.id} className='timeline-item mb-5 position-relative ps-5'>
                    {/* Timeline Dot */}
                    <span
                      className='position-absolute top-0 start-0 translate-middle bg-primary rounded-circle'
                      style={{width: '14px', height: '14px'}}
                    ></span>

                    {/* News Card */}
                    <div className='p-4 bg-light rounded-4 shadow-sm w-100'>
                      {/* Title */}
                      <h4 className='fw-bold text-primary mb-1'>{item.title}</h4>

                      {/* Date */}
                      <div className='text-muted small mb-3 fs-5'>
                        <i className='fas fa-calendar-alt mx-1'></i>
                        {formatDate(item?.created_at)}
                      </div>

                      {/* Content */}
                      <p
                        className='card-text mb-3'
                        dangerouslySetInnerHTML={{
                          __html: processContent(item?.content, expandedNews[item.id]),
                        }}
                      ></p>

                      {/* Read More / Show Less */}
                      {item?.content?.length > 300 && (
                        <button
                          onClick={() => toggleExpand(item.id)}
                          className='btn btn-sm btn-outline-primary rounded-pill px-3 d-flex align-items-center'
                        >
                          {expandedNews[item.id] ? t('LABEL.SHOW_LESS') : t('LABEL.READ_MORE')}
                          <i
                            className={`fas fa-chevron-${
                              expandedNews[item.id] ? 'up' : 'right'
                            } ms-2`}
                          ></i>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default StudentDashboard
